import type { QueryClient } from "@tanstack/react-query";
import i18n from "i18next";
import { toast } from "sonner";
import { api } from "../../api/axios";
import { apiUrl } from "../../config/backendFeatures";
import { isNativeApp } from "../../native/platform";
import { useBlockStore } from "../../store/useBlockStore";
import type { ConversationDto, MessageDto } from "../contract/chat.events";
import { findConversation, type ConversationsData } from "../lib/conversationCache";
import { chatKeys } from "../lib/queryKeys";
import { useConversationPrefs } from "../store/conversationPrefsStore";
import { useChatPopup } from "../store/popupStore";

/**
 * «Вам написали» while the site is open:
 *  - tab visible, another page/chat → an in-app toast with «Открыть»;
 *  - tab in the background → a system notification (if allowed).
 * With the site closed only the server can notify (Web Push / FCM / e-mail —
 * docs/BACKEND_TASKS.md B18); `subscribeWebPush` hands it the subscription.
 */

const ENV_PUSH_KEY = import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY as string | undefined;
let serverKey: Promise<string | null> | null = null;

/** VAPID public key: from the build, else asked from the server once (null — no web push there). */
function pushKey(): Promise<string | null> {
  if (ENV_PUSH_KEY) return Promise.resolve(ENV_PUSH_KEY);
  serverKey ??= api
    .get(apiUrl("/api/notifications/web-push/public-key"))
    .then((res) => (typeof res.data?.publicKey === "string" ? res.data.publicKey : null))
    .catch(() => null);
  return serverKey;
}

export const notificationsSupported = (): boolean =>
  typeof window !== "undefined" && "Notification" in window && !isNativeApp();

/** The site's PWA worker (it imports public/chat-sw.js); none in dev. */
async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return (await navigator.serviceWorker.getRegistration()) ?? null;
  } catch {
    return null;
  }
}

// The router lives in the app; ChatPopupHost (inside it) registers navigate.
let navigateTo: (url: string) => void = (url) => window.location.assign(url);
export function setChatNavigator(fn: (url: string) => void): () => void {
  navigateTo = fn;
  return () => {
    if (navigateTo === fn) navigateTo = (url) => window.location.assign(url);
  };
}

function openUrl(url: string) {
  navigateTo(url);
}

/** The page asked the worker to open a chat (notification click on an open tab). */
export function listenForNotificationClicks(): () => void {
  if (!("serviceWorker" in navigator)) return () => undefined;
  const onMessage = (e: MessageEvent) => {
    const d = e.data as { type?: string; url?: string } | null;
    if (d?.type === "turaq:open" && d.url) openUrl(new URL(d.url).pathname);
  };
  navigator.serviceWorker.addEventListener("message", onMessage);
  return () => navigator.serviceWorker.removeEventListener("message", onMessage);
}

function looking(conversationId: string): boolean {
  if (document.visibilityState !== "visible") return false;
  return window.location.pathname === `/messages/${conversationId}` || useChatPopup.getState().conversationId === conversationId;
}

function preview(m: MessageDto): string {
  if (m.body) return m.body.length > 140 ? `${m.body.slice(0, 140)}…` : m.body;
  return m.type === "image" ? `📷 ${i18n.t("chat.photo")}` : "";
}

export function notifyIncoming(queryClient: QueryClient, message: MessageDto): void {
  const { conversationId } = message;
  if (looking(conversationId)) return;
  if (useConversationPrefs.getState().archived.includes(conversationId)) return;

  let conv: ConversationDto | null = queryClient.getQueryData<ConversationDto>(chatKeys.conversation(conversationId)) ?? null;
  for (const f of ["all", "unread"] as const) conv ??= findConversation(queryClient.getQueryData<ConversationsData>(chatKeys.list(f)), conversationId);
  if (conv && useBlockStore.getState().blocked.some((u) => u.id === conv?.counterpart.id)) return;

  const name = conv?.counterpart.name || "Turaq.kg";
  const body = preview(message);
  const url = `/messages/${conversationId}`;

  if (document.visibilityState === "visible") {
    toast(name, {
      id: `chat:${conversationId}`,
      description: body,
      action: { label: i18n.t("chat.notify.open"), onClick: () => openUrl(url) },
    });
    return;
  }

  if (!notificationsSupported() || Notification.permission !== "granted") return;
  void registration().then((reg) => {
    const options: NotificationOptions = { body, icon: "/logo.png", tag: conversationId, data: { url } };
    if (reg) return reg.showNotification(name, options);
    const n = new Notification(name, options);
    n.onclick = () => {
      window.focus();
      openUrl(url);
      n.close();
    };
  });
}

/** Asks for permission (must run from a click). Resolves the final state. */
export async function enableNotifications(): Promise<NotificationPermission> {
  if (!notificationsSupported()) return "denied";
  const result = await Notification.requestPermission();
  if (result === "granted") void subscribeWebPush();
  return result;
}

/** Registers this browser for server pushes, when the backend has them (B18). */
export async function subscribeWebPush(): Promise<void> {
  if (!notificationsSupported() || Notification.permission !== "granted") return;
  const reg = await registration();
  if (!reg || !("pushManager" in reg)) return;
  const key = await pushKey();
  if (!key) return;
  try {
    const appKey = urlBase64ToUint8Array(key);
    let sub = await reg.pushManager.getSubscription();
    // Subscribed under an old key (keys rotated) — the server can't reach it.
    if (sub && !sameKey(sub.options.applicationServerKey, appKey)) {
      await sub.unsubscribe();
      sub = null;
    }
    sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appKey });
    await api.post(apiUrl("/api/notifications/web-push/subscribe"), sub.toJSON());
  } catch {
    /* blocked / unsupported — in-app notifications still work */
  }
}

/** Logout: this browser must stop receiving the previous user's pushes. */
export async function unsubscribeWebPush(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  try {
    const reg = await registration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    await api.post(apiUrl("/api/notifications/web-push/unsubscribe"), { endpoint: sub.endpoint }).catch(() => undefined);
    await sub.unsubscribe();
  } catch {
    /* nothing to undo */
  }
}

function sameKey(a: ArrayBuffer | null | undefined, b: Uint8Array): boolean {
  if (!a) return true;
  const x = new Uint8Array(a);
  return x.length === b.length && x.every((v, i) => v === b[i]);
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** B18 — «письмо о непрочитанном сообщении» on/off. null: the server has no settings yet. */
export async function getEmailNotifications(): Promise<boolean | null> {
  try {
    const res = await api.get(apiUrl("/api/notifications/settings"));
    return typeof res.data?.emailEnabled === "boolean" ? res.data.emailEnabled : null;
  } catch {
    return null;
  }
}

export async function setEmailNotifications(emailEnabled: boolean): Promise<boolean> {
  const res = await api.put(apiUrl("/api/notifications/settings"), { emailEnabled });
  return Boolean(res.data?.emailEnabled);
}
